// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Contexts
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";

// API
import { transferDeviceToClinic } from "../api";

export const useTransferDevice = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();
  const { devices, setDevices } = useDevicesContext();

  return useMutation({
    mutationFn: async (payload: {
      deviceId: string;
      targetClinicId: string;
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return transferDeviceToClinic(
          token,
          clinicId,
          payload.deviceId,
          payload.targetClinicId
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      setDevices((devices ?? []).filter((d) => d.id !== variables.deviceId));

      queryClient.invalidateQueries({ queryKey: ["clinic-devices", clinicId] });
      setSnackbarInfo({
        variant: "success",
        message: t("Device transferred successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Device could not be transferred!"),
      });
    },
  });
};
